import React, { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BsBoxes } from "react-icons/bs";
import useGetModulesData from "@/hooks/useGetModulesData";
import {
    getClusterCreationTableConfig,
    getCampaignCreationKeywordsTableConfig,
    getCampaignCreationCategoriesTableConfig,
    getCampaignCreationAssetsTableConfig,
} from "@ts/reviniti/widget-configs";
import {
    productDataSorter,
    getCategoryOptions,
    getSubcategoryOptions,
    enrichAdditionalFiltersWithCategoryData,
} from "@ts/utils";
import {
    BCC_MODULE_TYPES,
    BCC_KEYWORD_TYPES,
    IBCCKeyword,
    IChannel,
    KeywordTypeValues,
    KWD_BID_TYPES,
    BCC_BID_SUGGESTION_TYPES,
    CommonWidgetMetrics,
    IBCCProductSkuDetail,
    IBCCModalConfig,
    KEYWORD_MODAL_ACTIVE_TABS,
    BCC_MODAL_TYPES,
    ICampaignCreationContext,
} from "@ts/common-types";
import AddNegativeKeywords from "@/pages/campaign-creation/AddNegativeKeywords";
import AddCustomKeywordButton from "@/pages/campaign-creation/AddCustomKeywordButton";
import { toggleVariantSelected } from "@/features/clustersSlice";
import {
    setVariantSelectionInState,
    getVariantSelectionFromState,
} from "@components/widgets/WidgetCellRenderers";
import CsvUploadButton from "@/pages/campaign-creation/CsvUploadButton";

interface ModalLogicReturn {
    computed?: {
        // Standardized table configurations
        tableConfig?: any;              // Primary table config (all modals)
        tableConfigSecondary?: any;     // Secondary table config (e.g., Categories in Keywords modal)
        
        // Common computed values
        productDataSorterFunction?: any;
        productDataFilter?: any;
        selectedCountLabel?: any;
        tabs?: any;
    };
    handlers: {
        onSave?: () => void;
        onCancel?: () => void;
        onTableSelection?: any;
        onRowSelect?: any;
    };
    validation: {
        isSaveDisabled: boolean;
    };
    loading?: {
        isLoading: boolean;
        isSaving: boolean;
    };
    footer?: {
        saveButtonLoading?: boolean;
    };
    metadata?: {
        selectedRows: any[];
        contextData?: any;
    };
}

const useGenericModalComposer = (
    baseConfig: any,
    logic: ModalLogicReturn,
    context: ICampaignCreationContext
): IBCCModalConfig => {
    return useMemo(() => {
        return {
            context,
            ...baseConfig,
            
            // Merge table configuration (now standardized!)
            tableConfig: {
                ...baseConfig.tableConfig,
                ...(logic.computed?.tableConfig && { primary: logic.computed.tableConfig }),
                ...(logic.computed?.tableConfigSecondary && { 
                    secondary: logic.computed.tableConfigSecondary 
                }),
                ...(baseConfig.tableConfig.sorter?.enabled && logic.computed?.productDataSorterFunction && {
                    sorter: {
                        ...baseConfig.tableConfig.sorter,
                        customSorter: logic.computed.productDataSorterFunction,
                    },
                }),
            },
            
            // Dynamic tabs (if provided by logic - for Keywords modal)
            ...(logic.computed?.tabs && { tabs: logic.computed.tabs }),
            
            // Merge handlers
            handlers: {
                ...logic.handlers,
            },
            
            // Merge validation
            validation: {
                saveDisabled: logic.validation.isSaveDisabled,
            },
            
            // Merge footer
            footer: {
                ...baseConfig.footer,
                ...(logic.computed?.selectedCountLabel && { 
                    selectedCountLabel: logic.computed.selectedCountLabel 
                }),
                ...(logic.footer?.saveButtonLoading !== undefined && { 
                    saveButtonLoading: logic.footer.saveButtonLoading 
                }),
            },
            
            // Merge loading states
            loading: {
                isLoading: logic.loading?.isLoading ?? false,
                isSaving: logic.loading?.isSaving ?? false,
            },
            
            // Merge metadata
            metadata: {
                selectedRows: logic.metadata?.selectedRows || [],
                contextData: logic.metadata?.contextData || {},
            },
        };
    }, [baseConfig, logic, context]);
};

const useClusterModalConfig = (context: ICampaignCreationContext): IBCCModalConfig => {
    const logic = useClusterLogic(context);
    const config = useGenericModalComposer(CLUSTER_BASE_CONFIG, logic, context);
    
    // Add cluster-specific footer label
    return useMemo(() => ({
        ...config,
        footer: {
            ...config.footer,
            selectedCountLabel: (count: number) => `Selected Products (${count})`,
        },
    }), [config]);
};


// Cluster creation modal config
const CLUSTER_BASE_CONFIG = {
    modalId: "cluster-creation-modal",
    moduleType: BCC_MODULE_TYPES.CLUSTER,
    
    ui: {
        showHeader: true,
        showHeaderInput: true,
        showTabBar: false,
        showCustomKeywordButton: false,
        showFooterSelectedCount: true,
        modalWidth: 1016,
        modalHeight: 600,
        contentHeight: 315,
    },
    
    header: {
        title: "Create Cluster",
        icon: <BsBoxes />,
        iconStyles: {
            containerWidth: "32px",
            containerHeight: "32px",
            containerBgColor: "#f5f5f5",
            iconSize: "18px",
            iconColor: "#252b37",
        },
    },
    
    tableConfig: {
        dataSource: {
            key: "productGroups",
        },
        filters: {
            enabled: true,
            categoryFilter: true,
            subcategoryFilter: true,
        },
        sorter: {
            enabled: true,
        },
    },
    
    footer: {
        showSelectedCount: true,
        saveButtonText: "Save and Add Cluster",
    },
};

const useClusterRowSelectHandler = () => {
    const dispatch = useDispatch();

    return useCallback(
        (record: any, selected: boolean, _allSelectedRows: any[]) => {
            const productId = record?.id || record?.sku || "";
            if (!productId) return;

            // Determine clusterId - use record's clusterId or default format for modal products
            const clusterId =
                record?.clusterId ||
                (productId ? `##productCluster_${productId}` : "default");

            const skuDetails = record?.skuDetails || record?.skuList || [];
            const variantLabels: string[] = [];

            if (Array.isArray(skuDetails) && skuDetails.length > 0) {
                // Extract variant labels from skuDetails
                skuDetails.forEach((skuDetail: IBCCProductSkuDetail) => {
                    const variantLabel = skuDetail?.unit || skuDetail?.sku || "";
                    if (variantLabel) {
                        variantLabels.push(variantLabel);
                    }
                });
            } else if (record?.[`${CommonWidgetMetrics.VARIANTS}_value`]) {
                // Fallback: use variants value from record
                const variantsValue =
                    record[`${CommonWidgetMetrics.VARIANTS}_value`];
                if (Array.isArray(variantsValue)) {
                    variantLabels.push(...variantsValue);
                }
            }

            if (variantLabels.length === 0) return;

            // Set all variants to active/inactive based on selection
            variantLabels.forEach((variantLabel: string) => {
                if (variantLabel) {
                    const currentState = getVariantSelectionFromState(
                        clusterId,
                        productId,
                        variantLabel
                    );

                    // Only update if the current state doesn't match desired state
                    if (currentState !== selected) {
                        setVariantSelectionInState(
                            clusterId,
                            productId,
                            variantLabel,
                            selected
                        );

                        if (
                            Array.isArray(record.skuDetails) &&
                            record.skuDetails.length > 0
                        ) {
                            const skuDetail = record.skuDetails.find(
                                (s: IBCCProductSkuDetail) => {
                                    const unitLabel = (s.unit || "").toString();
                                    const skuLabel = (s.sku || "").toString();
                                    return (
                                        unitLabel === variantLabel ||
                                        skuLabel === variantLabel
                                    );
                                }
                            );
                            if (skuDetail) {
                                skuDetail.isActiveForSession = selected;
                            }
                        }

                        dispatch(
                            toggleVariantSelected({
                                clusterId: clusterId,
                                productId: productId,
                                variantLabel: variantLabel,
                            })
                        );
                    }
                }
            });

            window.dispatchEvent(new CustomEvent("variantSelectionUpdated"));
        },
        [dispatch]
    );
};

const useClusterLogic = (context: ICampaignCreationContext): ModalLogicReturn => {
    const handleClusterRowSelect = useClusterRowSelectHandler();

    // State management - multi-select for categories and subcategories
    const [categoryFilters, setCategoryFilters] = React.useState<{
        selectedCategories: string[];
        selectedSubcategories: string[];
    }>({
        selectedCategories: [],
        selectedSubcategories: [],
    });

    // Get base table config
    const baseTableConfig = useMemo(() => getClusterCreationTableConfig(), []);

    // Computed values
    const categoryOptions = useMemo(() => {
        return getCategoryOptions(
            context.productGroups || [],
            baseTableConfig?.additionalFilters
        );
    }, [context.productGroups, baseTableConfig?.additionalFilters]);

    const subcategoryOptions = useMemo(() => {
        return getSubcategoryOptions(
            context.productGroups || [],
            baseTableConfig?.additionalFilters
        );
    }, [context.productGroups, baseTableConfig?.additionalFilters]);

    const filteredSubcategoryOptions = useMemo(() => {
        if (!context.productGroups || !Array.isArray(context.productGroups)) {
            return subcategoryOptions;
        }

        // If categories are selected, restrict subcategories to those belonging to the selected categories
        let dataToUse = context.productGroups;

        if (categoryFilters.selectedCategories.length > 0) {
            const selectedCategorySet = new Set(categoryFilters.selectedCategories);
            dataToUse = dataToUse.filter((item) => 
                selectedCategorySet.has(item.category)
            );
        }

        const uniqueSubcategories = [
            ...new Set(
                dataToUse
                    .map((item) => item.subcategory)
                    .filter(
                        (subcat) =>
                            subcat && subcat !== "-" && subcat.trim() !== ""
                    )
            ),
        ];

        return uniqueSubcategories.map((subcat) => ({
            label: subcat,
            value: subcat,
        }));
    }, [
        context.productGroups,
        categoryFilters.selectedCategories,
        subcategoryOptions,
    ]);

    const additionalFiltersWithData = useMemo(() => {
        // First, enrich filters with category data (this sets up basic structure)
        // Note: We pass empty strings instead of "All" since we removed the "All" option
        const enrichedFilters = enrichAdditionalFiltersWithCategoryData(
            baseTableConfig?.additionalFilters || [],
            categoryOptions,
            filteredSubcategoryOptions,
            {
                selectedCategory: categoryFilters.selectedCategories.length > 0 
                    ? categoryFilters.selectedCategories[0] 
                    : "",
                selectedSubcategory: categoryFilters.selectedSubcategories.length > 0
                    ? categoryFilters.selectedSubcategories[0]
                    : "",
            },
            undefined, // We'll handle onChange ourselves
            CommonWidgetMetrics.All
        );

        // Now modify filters to support multi-select
        return enrichedFilters.map((filter) => {
            if (filter.key === CommonWidgetMetrics.CATEGORY && filter?.props) {
                return {
                    ...filter,
                    props: {
                        ...filter.props,
                        mode: "multiple" as const,
                        value: categoryFilters.selectedCategories,
                        onChange: (values: string[]) => {
                            setCategoryFilters((prev) => {
                                // When categories change, remove subcategories that no longer belong
                                if (!context.productGroups || values.length === 0) {
                                    return {
                                        selectedCategories: values,
                                        selectedSubcategories: [],
                                    };
                                }

                                const allowedSubcategories = new Set(
                                    (context.productGroups as any[])
                                        .filter((product) => values.includes(product.category))
                                        .map((product) => product.subcategory)
                                        .filter(
                                            (subcat) =>
                                                subcat && subcat !== "-" && subcat.trim() !== ""
                                        )
                                );

                                const filteredSubcategories = prev.selectedSubcategories.filter((subcat) =>
                                    allowedSubcategories.has(subcat)
                                );

                                return {
                                    selectedCategories: values,
                                    selectedSubcategories: filteredSubcategories,
                                };
                            });
                        },
                        placeholder: "Select Categories",
                    },
                };
            } else if (filter.key === CommonWidgetMetrics.SUB_CATEGORY && filter?.props) {
                return {
                    ...filter,
                    props: {
                        ...filter.props,
                        mode: "multiple" as const,
                        value: categoryFilters.selectedSubcategories,
                        onChange: (values: string[]) => {
                            setCategoryFilters((prev) => ({
                                ...prev,
                                selectedSubcategories: values,
                            }));
                        },
                        placeholder: "Select Subcategories",
                    },
                };
            }
            return filter;
        });
    }, [
        baseTableConfig?.additionalFilters,
        categoryOptions,
        filteredSubcategoryOptions,
        categoryFilters,
        context.productGroups,
    ]);

    const tableConfigWithFilters = useMemo(
        () => ({
            ...baseTableConfig,
            categoryOptions,
            subcategoryOptions,
            additionalFilters: additionalFiltersWithData,
        }),
        [
            baseTableConfig,
            categoryOptions,
            subcategoryOptions,
            additionalFiltersWithData,
        ]
    );

    // Handlers
    const productDataFilter = useCallback(
        <T extends { category?: string; subcategory?: string }>(
            data: T[]
        ): T[] => {
            let filtered = [...data];

            // Filter by selected categories (empty array means no filter)
            if (categoryFilters.selectedCategories.length > 0) {
                const categorySet = new Set(categoryFilters.selectedCategories);
                filtered = filtered.filter(
                    (item) => item.category && categorySet.has(item.category)
                );
            }

            // Filter by selected subcategories (empty array means no filter)
            if (categoryFilters.selectedSubcategories.length > 0) {
                const subcategorySet = new Set(categoryFilters.selectedSubcategories);
                filtered = filtered.filter(
                    (item) =>
                        item.subcategory && subcategorySet.has(item.subcategory)
                );
            }

            return filtered;
        },
        [categoryFilters.selectedCategories, categoryFilters.selectedSubcategories]
    );

    const productDataSorterFunction = useCallback(
        <T,>(
            data: T[],
            originalData: T[],
            sortConfig: {
                sorter?: (a: T, b: T) => number;
                order?: "ascend" | "descend";
            }
        ): T[] => {
            return productDataSorter(
                data,
                originalData,
                sortConfig,
                context.selectedKeywords,
                BCC_MODULE_TYPES.CLUSTER
            );
        },
        [context.selectedKeywords]
    );

    // Validation
    const isSaveDisabled = useMemo(() => {
        return (
            context.selectedProducts.length === 0 ||
            !context.clusterName.trim()
        );
    }, [context.selectedProducts.length, context.clusterName]);

    // Return standardized shape
    return {
        computed: {
            tableConfig: tableConfigWithFilters,  // Standardized key name
            productDataFilter,
            productDataSorterFunction,
        },
        handlers: {
            onTableSelection: context.handleProductSelection,
            onRowSelect: handleClusterRowSelect,
            onSave: () => context.handleSaveCluster(true),
            onCancel: context.handleModalCancel,
        },
        validation: {
            isSaveDisabled,
        },
        loading: {
            isLoading: context.isLoadingProducts,
            isSaving: context.isSavingCluster,
        },
        footer: {
            saveButtonLoading: context.isSavingCluster,
        },
        metadata: {
            selectedRows: context.selectedProducts,
            contextData: {
                productGroups: context.productGroups,
                dataFilter: productDataFilter,
                dataSorter: productDataSorterFunction,
            },
        },
    };
};


// Keywords selection modal config
const KEYWORDS_BASE_CONFIG = {
    modalId: "keywords-selection-modal",
    moduleType: BCC_MODULE_TYPES.KEYWORDS,
    
    ui: {
        showHeader: false,
        showHeaderInput: false,
        showTabBar: true,
        showCustomKeywordButton: true,
        showFooterSelectedCount: true,
        showCategoriesCount: true,
        modalWidth: 1016,
        modalHeight: 600,
        contentHeight: 345,
    },
    
    header: {
        title: "Select Targets",
        icon: <BsBoxes />,
        iconStyles: {
            containerWidth: "32px",
            containerHeight: "32px",
            containerBgColor: "#f5f5f5",
            iconSize: "18px",
            iconColor: "#252b37",
        },
        showCheckboxFilters: true,
        checkboxOptions: [
            { value: "all", label: "All" },
            { value: KeywordTypeValues.BRANDED, label: "Branded" },
            { value: KeywordTypeValues.GENERIC, label: "Generic" },
            { value: KeywordTypeValues.COMPETITION, label: "Competition" },
        ] as { value: string; label: string }[],
    },
    
    tableConfig: {
        dataSource: {
            key: "keywords",
        },
        sorter: {
            enabled: true,
        },
    },
    
    footer: {
        showSelectedCount: true,
        saveButtonText: "Add Targets",
    },
};

const useKeywordsLogic = (
    clientCurrencySymbol: string,
    context: ICampaignCreationContext,
    tableContainerRef?: React.RefObject<HTMLDivElement>
): ModalLogicReturn => {
    const minExactMatchBid =
        useGetModulesData({ type: "channel-options" })?.minExactMatchBid || 0;

    // Computed values - Table configs
    const keywordsTableConfig = useMemo(
        () =>
            getCampaignCreationKeywordsTableConfig({
                handleFieldChange: context.handleFieldChange,
                handleBatchKeywordFieldUpdates: context.handleBatchKeywordFieldUpdates,
                clientCurrencySymbol,
                selectedKeywords: context.selectedKeywords,
                selectedKwTypes: context.modalParams?.selectedKwTypes,
                minExactMatchBid,
            }),
        [
            context.handleFieldChange,
            context.handleBatchKeywordFieldUpdates,
            clientCurrencySymbol,
            context.selectedKeywords,
            context.modalParams?.selectedKwTypes,
            minExactMatchBid,
        ]
    );

    const categoriesTableConfig = useMemo(
        () =>
            getCampaignCreationCategoriesTableConfig({
                handleFieldChange: context.handleFieldChange,
                handleBatchCategoryFieldUpdates: context.handleBatchCategoryFieldUpdates,
                clientCurrencySymbol,
                selectedTargetCategories: context.selectedTargetCategories,
            }),
        [
            context.handleFieldChange,
            context.handleBatchCategoryFieldUpdates,
            clientCurrencySymbol,
            context.selectedTargetCategories,
        ]
    );

    // Computed values - Loading states
    const isCurrentClusterTargetsLoading = context.modalParams?.clusterId
        ? context.loadingClustersTargets?.[context.modalParams.clusterId]
        : false;
    
    const isCombinedLoading =
        isCurrentClusterTargetsLoading || context.isLoadingProducts;

    // Computed values - Filtered data
    const selectedSuggestedKeywords = useMemo(
        () =>
            context.selectedKeywords.filter(
                (kw: IBCCKeyword) =>
                    kw?.keyword_type !== BCC_KEYWORD_TYPES.NEGATIVE
            ),
        [context.selectedKeywords]
    );

    // Handlers - Sorter
    const productDataSorterFunction = useCallback(
        <T,>(
            data: T[],
            originalData: T[],
            sortConfig: {
                sorter?: (a: T, b: T) => number;
                order?: "ascend" | "descend";
            }
        ): T[] => {
            return productDataSorter(
                data,
                originalData,
                sortConfig,
                context.selectedKeywords,
                BCC_MODULE_TYPES.KEYWORDS
            );
        },
        [context.selectedKeywords]
    );

    // Validation logic - derived boolean value
    const isSaveDisabled = useMemo(() => {
        const regularKeywords = context.selectedKeywords.filter(
            (kw) => kw?.keyword_type !== BCC_KEYWORD_TYPES.NEGATIVE
        );

        const hasRegularKeywords = regularKeywords.length > 0;
        const hasCategories = context.selectedTargetCategories?.length > 0;

        // User must select at least one keyword or category
        if (!hasRegularKeywords && !hasCategories) return true;

        // No longer require keywords for each selected type - user can select any type

        const isInvalid = (
            value: IBCCKeyword,
            field: string,
            checkAgainstField?: string
        ) =>
            value?.[field] == null ||
            Number(value[field]) <
                Math.max(
                    Number(value[checkAgainstField]) || 0,
                    minExactMatchBid
                );

        if (
            hasRegularKeywords &&
            regularKeywords.some((kw: IBCCKeyword) =>
                isInvalid(
                    kw,
                    KWD_BID_TYPES.EXACT_MATCH_BID,
                    BCC_BID_SUGGESTION_TYPES.MIN_BID_LIMIT
                )
            )
        ) {
            return true;
        }

        if (
            hasCategories &&
            context.selectedTargetCategories.some((cat) =>
                isInvalid(cat, KWD_BID_TYPES.CPM_BID)
            )
        ) {
            return true;
        }

        return false;
    }, [
        context.selectedKeywords,
        context.selectedTargetCategories,
        minExactMatchBid,
    ]);

    // Selected count label
    const selectedCountLabel = useCallback(() => {
        return `Selected Keywords (${context.selectedKeywords.length}) and Categories (${context.selectedTargetCategories.length})`;
    }, [context.selectedKeywords.length, context.selectedTargetCategories.length]);

    // Build tabs configuration
    const tabs = useMemo(() => [
        {
            key: KEYWORD_MODAL_ACTIVE_TABS.SUGGESTED_KEYWORDS,
            label: "Suggested Keywords",
            content: {
                type: "table" as const,
                tableConfig: keywordsTableConfig,
                dataSource: context.keywords,
                selectedRows: selectedSuggestedKeywords,
                isLoading: isCombinedLoading,
                dataSorter: productDataSorterFunction,
            },
            extraContent: (
                <>
                <AddCustomKeywordButton
                    onAdd={context.handleAddCustomKeyword}
                    keywords={context.keywords}
                    modalParams={context.modalParams}
                    tableConfig={keywordsTableConfig}
                    tableContainerRef={tableContainerRef}
                    />
                <CsvUploadButton
                    keywords={context.keywords}
                    selectedKeywords={context.selectedKeywords}
                    modalParams={context.modalParams}
                    handleKeywordSelection={context.handleKeywordSelection}
                    handleFieldChange={context.handleFieldChange}
                    handleBatchKeywordFieldUpdates={context.handleBatchKeywordFieldUpdates}
                    handleAddCustomKeyword={context.handleAddCustomKeyword}
                    tableConfig={keywordsTableConfig}
                />
                    </>
            ),
            handlers: {
                onTableSelection: context.handleKeywordSelection,
                onFieldChange: context.handleFieldChange,
            },
            isVisible: true,
        },
        {
            key: KEYWORD_MODAL_ACTIVE_TABS.NEGATIVE_KEYWORDS,
            label: "Negative Keywords",
            content: {
                type: "custom" as const,
                customComponent: (
                    <AddNegativeKeywords
                        handleKeywordSelection={
                            context.handleNegativeKeywordSelection
                        }
                        selectedKeywords={context.selectedKeywords}
                    />
                ),
            },
            handlers: {
                onTableSelection: context.handleNegativeKeywordSelection,
            },
            isVisible: true,
            footer: {
                showSelectedCount: true,
                selectedCountLabel,
                saveButtonText: "Add Targets",
                saveButtonLoading: false,
            },
        },
        {
            key: KEYWORD_MODAL_ACTIVE_TABS.CATEGORIES,
            label: "Categories",
            content: {
                type: "table" as const,
                tableConfig: categoriesTableConfig,
                dataSource: context.targetCategories,
                selectedRows: context.selectedTargetCategories,
                isLoading: isCombinedLoading,
            },
            handlers: {
                onTableSelection: context.handleCategorySelection,
                onFieldChange: context.handleFieldChange,
            },
            isVisible: true,
            footer: {
                showSelectedCount: true,
                selectedCountLabel,
                saveButtonText: "Add Targets",
                saveButtonLoading: false,
            },
        },
    ], [
        keywordsTableConfig,
        categoriesTableConfig,
        context.keywords,
        context.targetCategories,
        context.selectedTargetCategories,
        selectedSuggestedKeywords,
        isCombinedLoading,
        selectedCountLabel,
        tableContainerRef,
        productDataSorterFunction,
    ]);

    // Return standardized shape
    return {
        computed: {
            tableConfig: keywordsTableConfig,              // Standardized key name
            tableConfigSecondary: categoriesTableConfig,   // Standardized key name
            productDataSorterFunction,
            selectedCountLabel,
            tabs,
        },
        handlers: {
            onSave: context.handleSaveKeywords,
            onCancel: context.handleModalCancel,
        },
        validation: {
            isSaveDisabled,
        },
        loading: {
            isLoading: isCombinedLoading,
            isSaving: false,
        },
        footer: {
            saveButtonLoading: false,
        },
        metadata: {
            selectedRows: context.selectedKeywords,
            contextData: {
                keywords: context.keywords,
                targetCategories: context.targetCategories,
                modalParams: context.modalParams,
                dataSorter: productDataSorterFunction,
            },
        },
    };
};

const useKeywordsModalConfig = (
    clientCurrencySymbol: string,
    context: ICampaignCreationContext,
    tableContainerRef?: React.RefObject<HTMLDivElement>
): IBCCModalConfig => {
    const logic = useKeywordsLogic(clientCurrencySymbol, context, tableContainerRef);
    const config = useGenericModalComposer(KEYWORDS_BASE_CONFIG, logic, context);
    
    // Add keywords-specific metadata
    return useMemo(() => ({
        ...config,
        metadata: {
            ...config.metadata,
            modalParams: context.modalParams,
        },
    }), [config, context.modalParams]);
};


// Assets modal config
const ASSETS_BASE_CONFIG = {
    modalId: "assets-modal",
    moduleType: BCC_MODULE_TYPES.CLUSTER,
    
    ui: {
        showHeader: true,
        showHeaderInput: false,
        showTabBar: false,
        showCustomKeywordButton: false,
        showFooterSelectedCount: false,
        modalWidth: 1016,
        modalHeight: 600,
        contentHeight: 400,
        tableHeaderJustifyContent: "justify-content-end",
    },
    
    header: {
        title: "Add Assets",
        icon: <BsBoxes />,
        iconStyles: {
            containerWidth: "32px",
            containerHeight: "32px",
            containerBgColor: "#f5f5f5",
            iconSize: "18px",
            iconColor: "#252b37",
        },
    },
    
    tableConfig: {
        dataSource: {
            key: "assets",
        },
    },
    
    footer: {
        showSelectedCount: false,
        saveButtonText: "Add Assets",
    },
};

const useAssetsLogic = (
    clientCurrencySymbol: string,
    context: ICampaignCreationContext
): ModalLogicReturn => {
    // Computed values - Table config
    const assetsTableConfig = useMemo(
        () =>
            getCampaignCreationAssetsTableConfig({
                handleFieldChange: context.handleFieldChange,
                clientCurrencySymbol,
                selectedTargetCategories: context.selectedTargetCategories,
            }),
        [
            context.handleFieldChange,
            clientCurrencySymbol,
            context.selectedTargetCategories,
        ]
    );

    // Handlers
    const handleSave = useCallback(() => {
        console.log("Save assets");
        context.handleModalCancel();
    }, [context]);

    // Return standardized shape
    return {
        computed: {
            tableConfig: assetsTableConfig,  // Standardized key name
        },
        handlers: {
            onSave: handleSave,
            onCancel: context.handleModalCancel,
        },
        validation: {
            isSaveDisabled: false,
        },
        loading: {
            isLoading: false,
            isSaving: false,
        },
        footer: {
            saveButtonLoading: false,
        },
        metadata: {
            selectedRows: [],
            contextData: {
                assets: context.assets,
            },
        },
    };
};

const useAssetsModalConfig = (
    clientCurrencySymbol: string,
    context: ICampaignCreationContext
): IBCCModalConfig => {
    const logic = useAssetsLogic(clientCurrencySymbol, context);
    return useGenericModalComposer(ASSETS_BASE_CONFIG, logic, context);
};

/**
 * Unified modal config hook - computes all configs but returns only the needed one
 * 
 * Note: This calls all config hooks unconditionally (required by Rules of Hooks).
 * Each config is memoized, so recomputation only happens when dependencies change.
 */
export const useModalConfig = (
    modalType: BCC_MODAL_TYPES,
    context: ICampaignCreationContext,
    tableContainerRef?: React.RefObject<HTMLDivElement>
): IBCCModalConfig | null => {
    const clientCurrencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );
    
    const clusterConfig = useClusterModalConfig(context);
    const keywordsConfig = useKeywordsModalConfig(clientCurrencySymbol, context, tableContainerRef);
    const assetsConfig = useAssetsModalConfig(clientCurrencySymbol, context);

    switch (modalType) {
        case BCC_MODAL_TYPES.CLUSTER:
            return clusterConfig;
        case BCC_MODAL_TYPES.KEYWORDS:
            return keywordsConfig;
        case BCC_MODAL_TYPES.ASSETS:
            return assetsConfig;
        default:
            return null;
    }
};

